import { useState, useCallback, useEffect, useRef } from "react";
import { Target, Gauge, Layers, Rocket, Flame, Volume2, VolumeX, CalendarCheck, Crown, RotateCcw, Gem } from "lucide-react";
import { toast } from "sonner";
import { playEvenOdd, playContract, lastDigit, lastDigitsOf, SYMBOL } from "@/lib/evenOdd";
import { publicSend } from "@/lib/derivPublic";
import { api } from "@/lib/api";
import { sfxWin, sfxLoss, setArcadeMuted, isArcadeMuted } from "@/lib/arcadeSound";
import PenaltyShootout from "@/components/arcade/PenaltyShootout";
import PenaltyParty from "@/components/arcade/PenaltyParty";
import { useAuth } from "@/hooks/useAuth";
import { authHeader } from "@/lib/adminAuth";
import { flagEmoji } from "@/lib/country";
import TradersByCountry from "@/components/TradersByCountry";
import StreakJackpot from "@/components/arcade/StreakJackpot";
import RedBlackFlip from "@/components/arcade/RedBlackFlip";
import CarRace from "@/components/arcade/CarRace";
import BasketballShot from "@/components/arcade/BasketballShot";
import GemRush from "@/components/arcade/GemRush";
import DigitTape from "@/components/arcade/DigitTape";
import BoxingKO from "@/components/arcade/BoxingKO";
import LuckyNumber from "@/components/arcade/LuckyNumber";
import GreenLight from "@/components/arcade/GreenLight";
import DiceDuel from "@/components/arcade/DiceDuel";
import { burstFlag, burstDemoFree } from "@/components/arcade/AutoRun";
import SmartStake from "@/components/arcade/SmartStake";
import StakeGuardDialog from "@/components/StakeGuardDialog";
import { useStakeGuard } from "@/hooks/useStakeGuard";
import { requireEmail } from "@/lib/emailGate";
import { getRecovTarget } from "@/lib/recovery";
import EmailOptIn from "@/components/EmailOptIn";
import { tr } from "@/lib/tr";
import { useDigitTape } from "@/hooks/useDigitTape";
import { getLiveMarket, onLiveMarket, setLiveMarket } from "@/lib/liveMarket";

/** VTS Earnings Floor — 4 games, ONE engine: real Even/Odd contracts
 * (~×1.95). Shared stake, live session P&L, streak, leaderboard, badges. */
const GAMES = [
  { key: "penalty", label: "Penalty Shootout", Icon: Target, desc: "Pick a corner, beat the keeper" },
  { key: "jackpot", label: "Slot Machine", Icon: Gauge, desc: "Triple 7s · 5-win jackpot" },
  { key: "redblack", label: "Red / Black", Icon: Layers, desc: "Card flip · red=even black=odd" },
  { key: "carrace", label: "Car Race", Icon: Gauge, desc: "Red vs Blue · Even/Odd or Rise/Fall modes" },
  { key: "bball", label: "Basketball Shot", Icon: Target, desc: "Rise = swish · Fall = miss · 1 tick" },
  { key: "gems", label: "Gem Rush", Icon: Gem, desc: "Match gems on 90% odds · cascades" },
  { key: "boxing", label: "Boxing KO", Icon: Flame, desc: "Land the punch · rise or fall" },
  { key: "lucky", label: "Digit Mines", Icon: Target, desc: "Mines board · 💣 picks · cash-out pot" },
  { key: "greenlight", label: "Green Light", Icon: Gauge, desc: "Tap GO on green · reflex runs" },
  { key: "dice", label: "Dice Duel", Icon: Layers, desc: "Over/Under the bar · 50%+ sides only" },
];

/* 🏁 SEASONS — GLOBAL hard cap of 50 plays per ROLLING 60 minutes across ALL
   arcade games (a play frees its slot exactly one hour after it fired — no
   surprise clock-hour resets, and switching games does NOT dodge the cap).
   Honest reason: every play carries a house edge, so the longer you run the
   arcade the more it claws profits back. */
const SEASON_MAX = 100; // raised from 50 (owner request, Nov 2026)
const SEASON_MS = 3600 * 1000;
const prunePlays = (arr, now) => (arr || []).filter((t) => now - t < SEASON_MS);
const loadSeason = () => {
  try {
    localStorage.removeItem("vts_arcade_season_v2"); // legacy per-game store
    const s = JSON.parse(localStorage.getItem("vts_arcade_season_v3") || "null");
    if (s && Array.isArray(s.plays)) return { plays: prunePlays(s.plays, Date.now()) };
  } catch { /* storage unavailable */ }
  return { plays: [] };
};

/* ⚠️ OPTIONAL CAP OVERRIDE — the 20/hour cap can be switched OFF, but only by
   typing the exact risk phrase. It auto re-arms after 24 hours. Loss lock and
   stake caps are NOT affected — they always stay on. */
const CAPOFF_KEY = "vts_season_capoff_until";
const CAPOFF_MS = 24 * 3600 * 1000;
const CAPOFF_PHRASE = "I ACCEPT THE RISK";
const loadCapOff = () => {
  try { const v = Number(localStorage.getItem(CAPOFF_KEY)) || 0; return v > Date.now() ? v : 0; } catch { return 0; }
};

const LB_SECTIONS = [
  { key: "goal_streak", label: "Goal streaks", unit: "goals" },
  { key: "slot_run", label: "Slot runs", unit: "wins" },
  { key: "arena_series", label: "Arena series wins", unit: "" },
];

const ArcadeHub = ({ authorized, accountId, currency = "USD", isReal = false, balance = 0, onConnect }) => {
  const { session } = useAuth();
  const [party, setParty] = useState(null);   // ⚽ live copy-kick room state
  const partyRef = useRef(null);
  const [game, setGame] = useState("penalty");
  const [stake, setStake] = useState("0.35");
  const [busy, setBusy] = useState(false);
  // 💡 Smart Stake — auto-opens once per session before the first game.
  // If the post-connect email dialog is pending (no stored email), wait for
  // it to close first so the two dialogs never stack.
  const [smartOpen, setSmartOpen] = useState(false);
  useEffect(() => {
    if (!authorized || sessionStorage.getItem("vts_smartstake_v1") === "1") return undefined;
    let t = null;
    const openIt = () => {
      sessionStorage.setItem("vts_smartstake_v1", "1");
      t = setTimeout(() => setSmartOpen(true), 800);
    };
    let hasEmail = false;
    try {
      hasEmail = (localStorage.getItem("vts_email_optin_v1") || "").includes("@")
        || localStorage.getItem("vts_email_known_v1") === "1";
    } catch { /* noop */ }
    if (hasEmail) { openIt(); return () => clearTimeout(t); }
    window.addEventListener("vts-email-prompt-done", openIt, { once: true });
    return () => { window.removeEventListener("vts-email-prompt-done", openIt); clearTimeout(t); };
  }, [authorized]);
  const [net, setNet] = useState(0);
  const netRef = useRef(0);
  useEffect(() => { netRef.current = net; }, [net]);
  // 🛑 session loss lock — hitting it forces a 1-HOUR break for ALL games (real only)
  const [lossLock, setLossLock] = useState(() => { try { const v = Number(localStorage.getItem("vts_arcade_losslock")); return v >= 1 ? v : 10; } catch { return 10; } });
  const lossLockRef = useRef(10);
  useEffect(() => {
    lossLockRef.current = lossLock;
    try { localStorage.setItem("vts_arcade_losslock", String(lossLock)); } catch { /* noop */ }
  }, [lossLock]);
  // ⏳ forced break timestamp + loss baseline (so the lock re-arms fresh after each break)
  const [lockUntil, setLockUntil] = useState(0);
  const [lossBase, setLossBase] = useState(0);
  const lockUntilRef = useRef(0);
  const lossBaseRef = useRef(0);
  useEffect(() => { lockUntilRef.current = lockUntil; }, [lockUntil]);
  useEffect(() => { lossBaseRef.current = lossBase; }, [lossBase]);
  useEffect(() => {
    if (!accountId) { setLockUntil(0); setLossBase(0); return; }
    try {
      setLockUntil(Number(localStorage.getItem(`vts_arcade_lockuntil_${accountId}`)) || 0);
      setLossBase(Number(localStorage.getItem(`vts_arcade_lossbase_${accountId}`)) || 0);
    } catch { setLockUntil(0); setLossBase(0); }
  }, [accountId]);
  const startLossBreak = useCallback(() => {
    const until = Date.now() + SEASON_MS;
    setLockUntil(until);
    lockUntilRef.current = until;
    try { localStorage.setItem(`vts_arcade_lockuntil_${accountId}`, String(until)); } catch { /* noop */ }
    toast.error(tr("losslock_title", { amt: Math.abs(netRef.current - lossBaseRef.current).toFixed(2) }), {
      description: tr("losslock_desc"),
      duration: 7000,
    });
  }, [accountId]);
  /* 🥶 COLD-STREAK BRAKE — 5 straight real losses = 5-minute pause on ALL
     arcade games. Streaks are normal randomness (no pair choice prevents
     them); the brake just stops rapid-fire bleeding into one. Always on. */
  /* 🎯 Sniper entry gating REMOVED (June 2026, user request) — every kick
     fires instantly, win or lose. No confirmation waits, no skips. */
  const lossLockCheck = useCallback(() => {
    if (!isReal) return true; // 🎓 demo = practice money, no locks
    const now = Date.now();
    if (lockUntilRef.current > now) {
      toast.error(tr("lossbreak_title", { mins: Math.max(1, Math.ceil((lockUntilRef.current - now) / 60000)) }), {
        description: tr("lossbreak_desc"),
        duration: 6000,
      });
      return false;
    }
    if (netRef.current - lossBaseRef.current <= -lossLockRef.current) {
      startLossBreak();
      return false;
    }
    return true;
  }, [isReal, startLossBreak]);
  // auto-start the break the moment session P&L crosses the lock (not just on the next tap)
  useEffect(() => {
    if (!isReal || !accountId) return;
    if (lockUntil > Date.now()) return;
    if (net - lossBase <= -lossLock) startLossBreak();
  }, [net, isReal, accountId, lossBase, lossLock, lockUntil, startLossBreak]);
  // break expiry — unlock and re-arm the loss lock from the current P&L
  useEffect(() => {
    if (!isReal || !accountId || !lockUntil) return undefined;
    const expire = () => {
      if (Date.now() < lockUntilRef.current) return;
      setLockUntil(0);
      const base = netRef.current;
      setLossBase(base);
      try {
        localStorage.removeItem(`vts_arcade_lockuntil_${accountId}`);
        localStorage.setItem(`vts_arcade_lossbase_${accountId}`, String(base));
      } catch { /* noop */ }
      toast.success("✅ Break over — the arcade is open again. Fresh loss-lock allowance from here; play smart.", { duration: 6000 });
    };
    const t = setInterval(expire, 15000);
    expire();
    return () => clearInterval(t);
  }, [isReal, accountId, lockUntil]);
  const [streak, setStreak] = useState(0);
  const [muted, setMuted] = useState(isArcadeMuted());
  const [history, setHistory] = useState([]);
  // 📊 TRUE per-game session totals — independent of the capped history list,
  //    so playing one game NEVER changes another game's numbers
  const [gameStats, setGameStats] = useState({});
  const bumpGame = useCallback((label, won, profit) => {
    setGameStats((s) => {
      const g = s[label] || { w: 0, l: 0, net: 0 };
      return { ...s, [label]: { w: g.w + (won ? 1 : 0), l: g.l + (won ? 0 : 1), net: Math.round((g.net + (Number(profit) || 0)) * 100) / 100 } };
    });
  }, []);
  // 📊 per-game history filter + archived last session (scoped PER ACCOUNT —
  //    a demo session must never show on the real account)
  const [histFilter, setHistFilter] = useState(null);
  const [lastSession, setLastSession] = useState(null);
  useEffect(() => {
    try { localStorage.removeItem("vts_arcade_last_session"); } catch { /* legacy global key */ }
    if (!accountId) { setLastSession(null); return; }
    try { setLastSession(JSON.parse(localStorage.getItem(`vts_arcade_last_session_${accountId}`)) || null); }
    catch { setLastSession(null); }
  }, [accountId]);
  const [dailyBadge, setDailyBadge] = useState(() => localStorage.getItem("vts_arcade_badge_daily3") === "1");
  const [board, setBoard] = useState(null);
  const [pScore, setPScore] = useState({ goals: 0, saves: 0, streak: 0 }); // ⚽ penalty score (persisted)
  const [stLoaded, setStLoaded] = useState(false); // 💾 server state hydrated
  // 📡 tape streams the EXACT market the scanner has locked for trading
  const [liveMkt, setLiveMkt] = useState(getLiveMarket());
  useEffect(() => onLiveMarket((m) => setLiveMkt(m)), []);
  const { digits, total } = useDigitTape(100, liveMkt.sym);
  const [lbTick, setLbTick] = useState(0);
  // 🏁 GLOBAL rolling-hour season (20 plays max across all games, slots free up one by one)
  const [season, setSeason] = useState(loadSeason);
  const seasonRef = useRef(season);
  useEffect(() => { seasonRef.current = season; }, [season]);
  // ⚠️ optional cap override — timestamp until which the 20/hour cap is OFF
  const [capOffUntil, setCapOffUntil] = useState(loadCapOff);
  const capOffRef = useRef(capOffUntil);
  useEffect(() => { capOffRef.current = capOffUntil; }, [capOffUntil]);
  const [capDlg, setCapDlg] = useState(false);
  const [capInput, setCapInput] = useState("");
  const disableCap = useCallback(() => {
    const until = Date.now() + CAPOFF_MS;
    setCapOffUntil(until);
    capOffRef.current = until;
    try { localStorage.setItem(CAPOFF_KEY, String(until)); } catch { /* noop */ }
    setCapDlg(false);
    setCapInput("");
    toast.error(tr("capoff_title"), { description: tr("capoff_desc"), duration: 9000 });
  }, []);
  const rearmCap = useCallback(() => {
    setCapOffUntil(0);
    capOffRef.current = 0;
    try { localStorage.removeItem(CAPOFF_KEY); } catch { /* noop */ }
    toast.success(tr("capon_title"), { duration: 4000 });
  }, []);
  useEffect(() => {
    const t = setInterval(() => {
      const s = { plays: prunePlays(seasonRef.current.plays, Date.now()) };
      seasonRef.current = s;
      setSeason(s);
      try { localStorage.setItem("vts_arcade_season_v3", JSON.stringify(s)); } catch { /* noop */ }
      // ⏰ 24h override expired → cap re-arms automatically
      if (capOffRef.current && capOffRef.current <= Date.now()) {
        setCapOffUntil(0);
        capOffRef.current = 0;
        try { localStorage.removeItem(CAPOFF_KEY); } catch { /* noop */ }
        toast.success(tr("capon_title"), { description: tr("capon_auto_desc"), duration: 6000 });
      }
    }, 30000);
    return () => clearInterval(t);
  }, []);
  // 🔥 daily flame streak — play any arcade game once per day to keep it alive
  const [dailyStreak, setDailyStreak] = useState(() => {
    try { return JSON.parse(localStorage.getItem("vts_daily_streak")) || { last: "", count: 0 }; }
    catch { return { last: "", count: 0 }; }
  });
  const markDailyStreak = useCallback(() => {
    setDailyStreak((prev) => {
      const today = new Date().toISOString().slice(0, 10);
      if (prev.last === today) return prev;
      const yest = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
      const count = prev.last === yest ? prev.count + 1 : 1;
      const next = { last: today, count };
      try { localStorage.setItem("vts_daily_streak", JSON.stringify(next)); } catch { /* noop */ }
      if (count > 1) toast.success(`🔥 ${count}-day streak — you showed up again!`, { description: "Play once tomorrow to keep the flame alive", duration: 4000 });
      else toast(`🔥 Day 1 — streak started. Come back tomorrow to grow the flame`, { duration: 3500 });
      return next;
    });
  }, []);
  const streakAlive = (() => {
    const today = new Date().toISOString().slice(0, 10);
    const yest = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
    return dailyStreak.last === today || dailyStreak.last === yest;
  })();
  const seasonCheck = useCallback(() => {
    const now = Date.now();
    const capOff = capOffRef.current > now;
    const cur = prunePlays(seasonRef.current.plays, now);
    if (isReal && !capOff && cur.length >= SEASON_MAX) {
      const mins = Math.max(1, Math.ceil((cur[0] + SEASON_MS - now) / 60000));
      toast.error(tr("season_title", { max: SEASON_MAX }), {
        description: tr("season_desc", { mins }),
        duration: 6500,
      });
      return false;
    }
    const s = { plays: [...cur, now] };
    seasonRef.current = s;
    setSeason(s);
    try { localStorage.setItem("vts_arcade_season_v3", JSON.stringify(s)); } catch { /* noop */ }
    markDailyStreak();
    // 🏁 that was the LAST play of the season — tell them WHY the pause exists, right now
    if (isReal && !capOff && s.plays.length >= SEASON_MAX) {
      toast(tr("season_end_title", { max: SEASON_MAX }), {
        description: tr("season_end_desc"),
        duration: 10000,
      });
    }
    return true;
  }, [markDailyStreak, isReal]);
  const [nick, setNick] = useState("");
  const [nickInput, setNickInput] = useState("");

  useEffect(() => {
    api.get("/arcade/leaderboard").then((r) => setBoard(r.data)).catch(() => {});
  }, [lbTick]);

  // 💾 restore the arcade session from the server — survives reloads and devices
  useEffect(() => {
    if (!accountId) { setStLoaded(true); return; }
    setStLoaded(false);
    api.get(`/arcade/state/${accountId}`).then((r) => {
      const d = r.data || {};
      setNet(Number(d.net) || 0);
      setStreak(Number(d.streak) || 0);
      setHistory(Array.isArray(d.history) ? d.history : []);
      setGameStats(d.game_stats && typeof d.game_stats === "object" ? d.game_stats : {});
      setPScore({ goals: Number(d.goals) || 0, saves: Number(d.saves) || 0, streak: Number(d.pstreak) || 0 });
    }).catch(() => {}).finally(() => setStLoaded(true));
  }, [accountId]);

  // 💾 save on every change (debounced) — never lose the session to a refresh
  useEffect(() => {
    if (!stLoaded || !accountId) return;
    const t = setTimeout(() => {
      api.post(`/arcade/state/${accountId}`, {
        net, streak, goals: pScore.goals, saves: pScore.saves,
        pstreak: pScore.streak, history: history.slice(0, 100), game_stats: gameStats,
      }).catch(() => {});
    }, 600);
    return () => clearTimeout(t);
  }, [stLoaded, accountId, net, streak, pScore, history, gameStats]);

  // 🎓 demo accounts practice freely — turn off burst cooldowns globally
  useEffect(() => { burstDemoFree.on = authorized && !isReal; }, [authorized, isReal]);

  const resetSession = () => {
    // 📊 archive the finished session (from TRUE per-game totals) so "last session" stays visible
    const games = Object.entries(gameStats).map(([label, g]) => ({ label, ...g })).sort((a, b) => b.net - a.net);
    if (games.length > 0 && accountId) {
      const arch = { net, plays: games.reduce((s, g) => s + g.w + g.l, 0), endedAt: new Date().toLocaleString(), games };
      setLastSession(arch);
      try { localStorage.setItem(`vts_arcade_last_session_${accountId}`, JSON.stringify(arch)); } catch { /* noop */ }
    }
    setNet(0); setStreak(0); setHistory([]); setGameStats({}); setPScore({ goals: 0, saves: 0, streak: 0 });
    setHistFilter(null);
    if (accountId) api.post(`/arcade/state/${accountId}/reset`).catch(() => {});
    toast.success("Arcade session reset — counters back to zero");
  };

  useEffect(() => {
    if (!accountId) return;
    api.get(`/pro-trade/display-name/${accountId}`).then((r) => setNick(r.data?.display_name || "")).catch(() => {});
  }, [accountId]);

  const saveNick = async () => {
    const name = nickInput.trim();
    if (!name) return;
    try {
      const r = await api.post("/pro-trade/display-name", { account_id: accountId, display_name: name });
      setNick(r.data.display_name);
      setNickInput("");
      toast.success(`Nickname claimed: ${r.data.display_name}`);
      setLbTick((t) => t + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save nickname — try another");
    }
  };

  const toggleMute = () => { const m = !muted; setMuted(m); setArcadeMuted(m); };

  // record a personal best on the leaderboard (fire-and-forget)
  const report = useCallback((kind, value) => {
    if (!accountId || !value) return;
    api.post("/arcade/score", { account_id: accountId, kind, value }).catch(() => {});
    setTimeout(() => setLbTick((t) => t + 1), 800);
  }, [accountId]);

  // big-win Telegram group post — backend enforces real-only + cooldown
  const celebrate = useCallback((kind, amount) => {
    if (!accountId) return;
    api.post("/arcade/celebrate", { account_id: accountId, is_real: !!isReal, kind, amount }).catch(() => {});
  }, [accountId, isReal]);

  const markWinDay = useCallback(() => {
    try {
      const days = new Set(JSON.parse(localStorage.getItem("vts_arcade_win_days") || "[]"));
      days.add(new Date().toISOString().slice(0, 10));
      localStorage.setItem("vts_arcade_win_days", JSON.stringify([...days]));
      if (days.size >= 3 && localStorage.getItem("vts_arcade_badge_daily3") !== "1") {
        localStorage.setItem("vts_arcade_badge_daily3", "1");
        setDailyBadge(true);
        toast.success("Badge unlocked: REGULAR EARNER — arcade wins on 3 different days");
      }
    } catch { /* storage unavailable */ }
  }, []);

  // 🎯 live stake ref — every kick reads the CURRENT box value, so editing
  // the stake mid-burst applies from the very next kick (no locked-in stake)
  const stakeRef = useRef(stake);
  useEffect(() => { stakeRef.current = stake; }, [stake]);
  // 🛡 HARD CEILING (June 2026): no single real-money play may stake more than
  // 10% of the account balance — recovery, one-shots and overrides included.
  const balanceRef = useRef(balance);
  useEffect(() => { balanceRef.current = balance; }, [balance]);
  const capToBalance = useCallback((amt) => {
    if (!isReal) return amt;
    const bal = Number(balanceRef.current) || 0;
    if (bal <= 0) return amt;
    // 10% standard — a consented Smart Stake deep plan may lift the planned
    // recovery kick to at most 25% of balance, never beyond
    const planKick = Number(recovRef.current.planKick) || 0;
    const allowance = Math.max(bal * 0.10, Math.min(planKick, bal * 0.25));
    const maxAmt = Math.max(0.35, Math.round(allowance * 100) / 100);
    if (amt > maxAmt) {
      toast(`🛡 Stake capped at $${maxAmt.toFixed(2)}`, {
        description: planKick > bal * 0.10
          ? "Your deep-risk plan allows up to 25% of balance on the planned recovery kick — never more."
          : "One play can never risk more than 10% of the account — this cap applies to recovery stakes too.",
        duration: 4500,
      });
      return maxAmt;
    }
    return amt;
  }, [isReal]);

  // 🛡 Stake Guard — "are you sure?" popup before a risky real-money stake
  const { guard: stakeGuard, check: guardCheck, decide: guardDecide } = useStakeGuard({ isReal, balanceRef });

  // 🔁 shared recovery — Penalty-style: after a loss the NEXT play is raised
  // to win the hole back (+20%); a win drops straight back to base. Applies
  // when the pills are ON, and ALWAYS during ⚡ Auto-Play ×3/×5/×10 bursts.
  const [recovOn, setRecovOn] = useState(() => { try { return localStorage.getItem("vts_arcade_recov_on") === "1"; } catch { return false; } });
  const [recovCap, setRecovCap] = useState(() => { try { const v = parseInt(localStorage.getItem("vts_arcade_recov_cap"), 10); return [3, 4, 5, 6].includes(v) ? v : 4; } catch { return 4; } });
  const [recovStep, setRecovStep] = useState(0);
  const [recovHole, setRecovHole] = useState(0);
  const recovRef = useRef({ on: false, cap: 3, step: 0, hole: 0, ratio: 0.95, measured: false, maxMult: 10 });
  useEffect(() => {
    try {
      const v = parseInt(localStorage.getItem("vts_arcade_recov_maxmult"), 10);
      if (v >= 10 && v <= 500) recovRef.current.maxMult = v;
      const pk = Number(localStorage.getItem("vts_arcade_plan_kick"));
      if (pk >= 0.35 && pk <= 5000) recovRef.current.planKick = pk;
    } catch { /* storage unavailable */ }
  }, []);
  useEffect(() => {
    recovRef.current.on = recovOn;
    if (!recovOn) { recovRef.current.step = 0; setRecovStep(0); }
    try { localStorage.setItem("vts_arcade_recov_on", recovOn ? "1" : "0"); } catch { /* storage unavailable */ }
  }, [recovOn]);
  useEffect(() => {
    recovRef.current.cap = recovCap;
    try { localStorage.setItem("vts_arcade_recov_cap", String(recovCap)); } catch { /* storage unavailable */ }
  }, [recovCap]);
  const recHoleStake = (hole) => {
    const ratio = recovRef.current.ratio || 0.95;
    return Math.max(0.35, Math.ceil(((hole * (1 + getRecovTarget())) / ratio) * 100) / 100);
  };
  // 📐 before the first measured win, seed the payout ratio from the contract
  // type so the first recovery stake is sized roughly right per game
  const seedRatio = (contractType, barrier) => {
    if (recovRef.current.measured) return;
    let r = 0.95;
    if (contractType === "CALL" || contractType === "PUT") r = 0.9;
    else if (contractType === "DIGITMATCH") r = 5.6;
    else if (contractType === "DIGITDIFF") r = 0.06;
    else if (contractType === "DIGITOVER" || contractType === "DIGITUNDER") {
      const b = Number(barrier);
      const p = contractType === "DIGITOVER" ? (9 - b) / 10 : b / 10;
      if (p > 0 && p < 1) r = Math.min(10, Math.max(0.2, Math.round((0.95 / p - 1) * 100) / 100));
    }
    recovRef.current.ratio = r;
  };
  const recovStake = useCallback((base, spread = 1) => {
    const active = recovRef.current.on || burstFlag.active;
    const hole = recovRef.current.hole || 0;
    if (!active || hole <= 0.009) return base;
    // spread > 1 = low-payout games (Over 1/2) recover the hole over N wins
    // instead of one monster stake
    const ratio = recovRef.current.ratio || 0.95;
    const rec = spread > 1
      ? Math.max(0.35, Math.ceil(((hole * (1 + getRecovTarget())) / ratio / spread) * 100) / 100)
      : recHoleStake(hole);
    // ⛔ hard brake: recovery may never exceed the planned ladder's final kick
    // computed with the GAME'S real payout ratio. Tripping it = the ladder
    // can't finish → PAUSE the burst and abort this play (no base-stake fire).
    const cap = recovRef.current.cap || 4;
    const tgt = 1 + getRecovTarget();
    let simHole = base, maxNeed = base;
    for (let k = 0; k < cap; k++) {
      const s = Math.max(0.35, Math.ceil(((simHole * tgt) / ratio) * 100) / 100);
      maxNeed = s;
      simHole = Math.round((simHole + s) * 100) / 100;
    }
    const allowed = Math.max(maxNeed * 1.02, base * (recovRef.current.maxMult || 10));
    if (rec > allowed) {
      recovRef.current.hole = 0;
      recovRef.current.step = 0;
      setRecovHole(0);
      setRecovStep(0);
      burstFlag.halt = true;
      toast.error(tr("recov_cap_title", { cap }), { description: tr("recov_cap_desc"), duration: 8000 });
      return null; // abort — never fire an unplanned base play after a dead ladder
    }
    return Math.max(base, rec);
  }, []);
  // 🆕 every burst starts FRESH at base — losses from an old burst are never
  // carried into the next one (prevents runaway stake escalation)
  useEffect(() => {
    burstFlag.endHook = () => {
      recovRef.current.hole = 0;
      recovRef.current.step = 0;
      setRecovHole(0);
      setRecovStep(0);
    };
    return () => { burstFlag.endHook = null; };
  }, []);
  // 💡 one-tap offer (recovery OFF): after a loss, offer to double ONLY the
  // next play — exactly like Penalty's recovery chip. Win clears the offer.
  const [recovOffer, setRecovOffer] = useState(null); // {next}
  const [oneShot, setOneShot] = useState(null);
  const oneShotRef = useRef(null);
  const armOneShot = useCallback((next) => {
    oneShotRef.current = next;
    setOneShot(next);
    setRecovOffer(null);
    toast.success(`🔁 Next play armed at $${next.toFixed(2)}`, { description: "one play only — a win recovers the loss, a miss costs more" });
  }, []);
  const recovAfter = useCallback((won, lastAmt, profit = 0) => {
    const active = recovRef.current.on || burstFlag.active;
    // remember this game's real payout ratio so recovery stakes are sized right
    if (won && lastAmt > 0 && profit > 0) {
      recovRef.current.ratio = Math.min(10, Math.max(0.2, Math.round((profit / lastAmt) * 100) / 100));
      recovRef.current.measured = true;
    }
    // hole is tracked on every non-custom play so a burst can pick it up
    if (won) {
      if (active && recovRef.current.hole > 0.009) toast.success(tr("recov_won"));
      recovRef.current.hole = 0;
      recovRef.current.step = 0;
      setRecovOffer(null);
    } else {
      recovRef.current.hole = Math.round(((recovRef.current.hole || 0) + lastAmt) * 100) / 100;
      if (!active) {
        // ⛔ one-shot offer obeys the same 10× base brake — no offer deeper than that
        const nxt = Math.round(lastAmt * 2 * 100) / 100;
        const b = Math.max(0.35, Number(stakeRef.current) || 0.35);
        if (nxt <= b * 10) setRecovOffer({ next: nxt });
        else { setRecovOffer(null); toast.error(tr("recov_brake"), { duration: 5000 }); }
      } else {
        const next = recovRef.current.step + 1;
        if (next > recovRef.current.cap) {
          // applies to recovery-pill plays AND auto-recovering bursts — a full
          // lost ladder always pauses, never silently restarts
          recovRef.current.step = 0;
          recovRef.current.hole = 0;
          burstFlag.halt = true; // 🛑 full ladder lost — stop any running burst, no auto-restart
          toast.error(tr("recov_cap_title", { cap: recovRef.current.cap }), { description: tr("recov_cap_desc"), duration: 8000 });
        } else {
          recovRef.current.step = next;
        }
      }
    }
    setRecovStep(recovRef.current.step);
    setRecovHole(recovRef.current.hole);
  }, []);

  // ⚽ poll the Penalty Party room every 3s while the arcade is mounted
  const loadParty = useCallback(async () => {
    try {
      const r = await api.get(`/arcade/party/status${accountId ? `?account_id=${accountId}` : ""}`);
      setParty(r.data);
      partyRef.current = r.data;
    } catch { /* room status unavailable */ }
  }, [accountId]);
  useEffect(() => {
    loadParty();
    const t = setInterval(loadParty, 3000);
    return () => clearInterval(t);
  }, [loadParty]);

  // 🔥 HOT MARKETS — scan all 10 volatility markets every 10s and keep each
  // pair's recent digits + tick direction. Before EVERY arcade entry we rank
  // the pairs for THAT exact contract (even/odd %, over/under digit freq,
  // rise/fall momentum) and fire on the best-looking one. Entry-quality
  // filter on recent samples only — it never predicts the next tick.
  const [hot, setHot] = useState([]);
  const focusRef = useRef(SYMBOL);
  const hotRef = useRef([]);
  const lastRouteRef = useRef(null);
  useEffect(() => {
    let stop = false;
    const MARKETS = [
      ["R_10", "Vol 10"], ["R_25", "Vol 25"], ["R_50", "Vol 50"],
      ["R_75", "Vol 75"], ["R_100", "Vol 100"],
      ["1HZ10V", "Vol 10 (1s)"], ["1HZ25V", "Vol 25 (1s)"], ["1HZ50V", "Vol 50 (1s)"],
      ["1HZ75V", "Vol 75 (1s)"], ["1HZ100V", "Vol 100 (1s)"],
    ];
    const scan = async () => {
      const rows = await Promise.all(MARKETS.map(async ([sym, mLabel]) => {
        try {
          const h = await publicSend({ ticks_history: sym, count: 30, end: "latest", style: "ticks" });
          const raw = h?.history?.prices || [];
          const prices = raw.map(Number);
          if (prices.length < 10) return null;
          const digits = lastDigitsOf(raw);
          const evens = digits.filter((d) => d % 2 === 0).length;
          const evenPct = Math.round((evens / digits.length) * 100);
          const ups = prices.slice(1).filter((p, i) => p > prices[i]).length;
          return {
            sym, label: mLabel, evenPct, heat: Math.abs(evenPct - 50),
            digits, upRatio: ups / Math.max(1, prices.length - 1),
          };
        } catch { return null; }
      }));
      if (stop) return;
      const ok = rows.filter(Boolean).sort((a, b) => b.heat - a.heat);
      if (ok.length) {
        setHot(ok);
        hotRef.current = ok;
        focusRef.current = ok[0].sym;
      }
    };
    scan();
    const t = setInterval(scan, 10000);
    return () => { stop = true; clearInterval(t); };
  }, []);

  // 🎯 contract-aware pair ranking at entry time (uses the freshest scan)
  const bestPairFor = useCallback((contractType, barrier) => {
    const rows = hotRef.current;
    if (!rows.length) return null;
    const b = barrier != null ? parseInt(barrier, 10) : null;
    const score = (r) => {
      const n = r.digits.length || 1;
      switch (contractType) {
        case "DIGITEVEN": return r.digits.filter((d) => d % 2 === 0).length / n;
        case "DIGITODD": return r.digits.filter((d) => d % 2 === 1).length / n;
        case "DIGITOVER": return r.digits.filter((d) => d > b).length / n;
        case "DIGITUNDER": return r.digits.filter((d) => d < b).length / n;
        case "DIGITDIFF": return r.digits.filter((d) => d !== b).length / n;
        case "DIGITMATCH": return r.digits.filter((d) => d === b).length / n;
        case "CALL": return r.upRatio;
        case "PUT": return 1 - r.upRatio;
        default: return 0.5;
      }
    };
    const ranked = rows.map((r) => ({ ...r, s: score(r) })).sort((a, b2) => b2.s - a.s);
    const top = ranked[0];
    if (top && top.sym !== lastRouteRef.current) {
      lastRouteRef.current = top.sym;
      setLiveMarket(top.sym, top.label); // 📡 tape follows the routed pair even before a game scanner locks
      toast.info(`🎯 Entry filter → ${top.label} · ${Math.round(top.s * 100)}% recent`, {
        description: tr("entry_filter_desc"), duration: 2400,
      });
    }
    return top;
  }, []);

  const runFlip = useCallback(async (side, customStake, label, flipSymbol) => {
    if (!authorized) { onConnect?.(); return null; }
    if (!requireEmail()) { toast.error("📧 Add your email to play", { description: "One time only — it unlocks every game." }); return null; }
    if (!lossLockCheck()) return null;
    if (!seasonCheck(label)) return null;
    const useRecov = customStake == null && label !== "Penalty Shootout";
    let amt0 = Math.max(0.35, Number(customStake ?? stakeRef.current) || 0.35);
    if (customStake == null) amt0 = await guardCheck(amt0, { recovOn: recovRef.current.on || burstFlag.active, depth: recovRef.current.cap });
    if (amt0 == null) return null; // guard superseded — play cancelled
    let amt = useRecov ? recovStake(amt0) : amt0;
    if (amt == null) return null; // ladder judged unfinishable — burst paused, no play fires
    if (useRecov && oneShotRef.current) { amt = oneShotRef.current; oneShotRef.current = null; setOneShot(null); }
    amt = capToBalance(amt);
    // 👑 leader kick while the party is LIVE → fires server-side for EVERYONE
    const pt = partyRef.current;
    if (label === "Penalty Shootout" && pt?.open && pt?.is_leader) {
      setBusy(true);
      try {
        const { data } = await api.post("/arcade/party/kick", { side, stake: amt }, { headers: authHeader() });
        const res = { won: data.leader.won, digit: data.leader.digit, profit: data.leader.profit, stake: data.leader.stake, side };
        setNet((n) => Math.round((n + res.profit) * 100) / 100);
        setStreak((s) => (res.won ? s + 1 : 0));
        bumpGame("Penalty Party", res.won, res.profit);
        if (res.won) markWinDay();
        setHistory((h) => [{ ...res, label: "Penalty Party", t: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) }, ...h].slice(0, 100));
        toast(`⚽ Party kick fired for ${data.fired} account${data.fired === 1 ? "" : "s"} — ${data.goals} goal${data.goals === 1 ? "" : "s"} / ${data.saves} save${data.saves === 1 ? "" : "s"}${data.synced ? "" : " · one player caught a different tick"}`, { duration: 3500 });
        return res;
      } catch (e) {
        toast.error(e?.response?.data?.detail || e?.message || "Party kick failed");
        return null;
      } finally {
        setBusy(false);
      }
    }
    setBusy(true);
    try {
      const picked = flipSymbol ? null : bestPairFor(side === "EVEN" ? "DIGITEVEN" : "DIGITODD", null);
      const sym = flipSymbol || picked?.sym || focusRef.current;
      const res = await playEvenOdd({
        side, stake: amt, currency, accountId, symbol: sym,
        source: "arcade", strategy: `Arcade ${label}`,
      });
      setNet((n) => Math.round((n + res.profit) * 100) / 100);
      setStreak((s) => (res.won ? s + 1 : 0));
      bumpGame(label, res.won, res.profit);
      if (useRecov) recovAfter(res.won, amt, res.profit);
      if (res.won) markWinDay();
      if (label !== "Penalty Shootout") { if (res.won) sfxWin(); else sfxLoss(); }
      setHistory((h) => [{ ...res, label, t: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) }, ...h].slice(0, 100));
      return res;
    } catch (e) {
      toast.error(e?.message || "Trade failed — try again");
      return null;
    } finally {
      setBusy(false);
    }
  }, [authorized, currency, accountId, onConnect, markWinDay, recovStake, recovAfter, seasonCheck, lossLockCheck, bestPairFor, bumpGame, capToBalance, guardCheck]);

  // 🖥 server-burst plays land here — same session history/tally as local plays
  const logExternal = useCallback((row) => {
    setNet((n) => Math.round((n + row.profit) * 100) / 100);
    setStreak((s) => (row.won ? s + 1 : 0));
    bumpGame(row.label, row.won, row.profit);
    if (row.won) markWinDay();
    setHistory((h) => [{ ...row, t: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) }, ...h].slice(0, 100));
  }, [bumpGame, markWinDay]);

  // 🎮 generic engine for the new games (CALL/PUT, DIGITMATCH, DIGITOVER/UNDER)
  const runContract = useCallback(async ({ contractType, barrier = null, side, label, customStake, symbol, spread = 1, duration = 1 }) => {
    if (!authorized) { onConnect?.(); return null; }
    if (!requireEmail()) { toast.error("📧 Add your email to play", { description: "One time only — it unlocks every game." }); return null; }
    if (!lossLockCheck()) return null;
    if (!seasonCheck(label)) return null;
    const useRecov = customStake == null;
    if (useRecov) seedRatio(contractType, barrier);
    let amt0 = Math.max(0.35, Number(customStake ?? stakeRef.current) || 0.35);
    if (customStake == null) amt0 = await guardCheck(amt0, { recovOn: recovRef.current.on || burstFlag.active, depth: recovRef.current.cap });
    if (amt0 == null) return null; // guard superseded — play cancelled
    let amt = useRecov ? recovStake(amt0, spread) : amt0;
    if (amt == null) return null; // ladder judged unfinishable — burst paused, no play fires
    if (useRecov && oneShotRef.current) { amt = oneShotRef.current; oneShotRef.current = null; setOneShot(null); }
    amt = capToBalance(amt);
    setBusy(true);
    try {
      const picked = symbol ? null : bestPairFor(contractType, barrier);
      const sym = symbol || picked?.sym || focusRef.current;
      const res = await playContract({
        contractType, barrier, stake: amt, currency, accountId, duration,
        symbol: sym, source: "arcade", strategy: `Arcade ${label}`,
      });
      const out = { ...res, side: side || contractType };
      setNet((n) => Math.round((n + out.profit) * 100) / 100);
      setStreak((s) => (out.won ? s + 1 : 0));
      bumpGame(label, out.won, out.profit);
      if (useRecov) recovAfter(out.won, amt, out.profit);
      if (out.won) { markWinDay(); sfxWin(); } else { sfxLoss(); }
      setHistory((h) => [{ ...out, label, t: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) }, ...h].slice(0, 100));
      return out;
    } catch (e) {
      toast.error(e?.message || "Trade failed — try again");
      return null;
    } finally {
      setBusy(false);
    }
  }, [authorized, currency, accountId, onConnect, markWinDay, recovStake, recovAfter, seasonCheck, lossLockCheck, bestPairFor, bumpGame, capToBalance, guardCheck]);

  return (
    <div data-testid="arcade-hub" className="space-y-4">
      {/* 🛡 Stake Guard — risky-stake confirmation with a safer suggestion */}
      <StakeGuardDialog guard={stakeGuard} currency={currency}
        onDecide={(v) => { if (stakeGuard && v !== stakeGuard.stake) setStake(v.toFixed(2)); guardDecide(v); }} />
      {/* 💡 Smart Stake — balance-sized stake / loss lock / recovery cap */}
      <SmartStake open={smartOpen} onOpenChange={setSmartOpen} balance={balance} currency={currency}
        onApply={(r) => {
          setStake(r.stake.toFixed(2));
          setLossLock(r.lossLock);
          setRecovCap(r.recovCap);
          recovRef.current.maxMult = r.maxMult || 10;
          recovRef.current.planKick = r.maxKick || 0;
          try {
            localStorage.setItem("vts_arcade_recov_maxmult", String(r.maxMult || 10));
            localStorage.setItem("vts_arcade_plan_kick", String(r.maxKick || 0));
          } catch { /* storage unavailable */ }
          toast.success(`💡 Smart setup applied — $${r.stake.toFixed(2)} stake · survive ${r.depth} losses in a row · $${r.lossLock} loss lock`, {
            description: `A win inside your ${r.depth}-loss depth targets everything back${Math.round((r.target ?? 0.05) * 100) > 0 ? ` +${Math.round((r.target ?? 0.05) * 100)}%` : ""}. Worst case if the whole ladder fails: ~$${r.worst.toFixed(2)} — that's your planned risk.`,
            duration: 7000,
          });
        }} />
      {/* live session bar — scrolls with the page so games stay visible */}
      <div className="rounded-xl border border-white/10 bg-[#0B0E14] p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="min-w-0">
            <p className="text-[9px] font-mono uppercase tracking-[0.3em] text-white/40">VTS Earnings Floor</p>
            <p className="text-sm font-black tracking-tight text-white">10 games · one engine · real Deriv contracts</p>
            {dailyBadge && (
              <span data-testid="arcade-badge-daily" className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full border border-[#089981]/50 bg-[#089981]/10 text-[9px] font-mono text-[#089981]">
                <CalendarCheck className="w-3 h-3" /> REGULAR EARNER
              </span>
            )}
            {streakAlive && dailyStreak.count > 0 && (
              <span data-testid="arcade-daily-streak" className="inline-flex items-center gap-1 mt-1 ml-1 px-2 py-0.5 rounded-full border border-[#F7B731]/60 bg-[#F7B731]/10 text-[9px] font-mono font-black text-[#F7B731]">
                🔥 {dailyStreak.count}-DAY STREAK{dailyStreak.last !== new Date().toISOString().slice(0, 10) ? " · PLAY TODAY TO KEEP IT" : ""}
              </span>
            )}
          </div>
          <div className="ml-auto flex items-center gap-5 flex-wrap">
            <label className="block">
              <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-white/40">Stake $</span>
              <input data-testid="arcade-stake-input" type="number" min="0.35" step="0.01" value={stake}
                onChange={(e) => setStake(e.target.value)}
                className="block w-24 mt-0.5 px-3 h-10 rounded-md bg-[#131722] border border-white/10 focus:border-[#2962FF] focus:outline-none text-sm font-mono text-white" />
              <button data-testid="arcade-smartstake-btn" onClick={() => setSmartOpen(true)}
                className="mt-1 px-2 py-0.5 rounded-full border border-[#F7B731]/50 bg-[#F7B731]/10 text-[9px] font-mono font-black text-[#F7B731] hover:bg-[#F7B731]/20 transition">
                💡 Smart stake
              </button>
            </label>
            <label className="block">
              <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-white/40">🛑 Loss lock $</span>
              <input data-testid="arcade-losslock-input" type="number" min="1" step="1" value={lossLock}
                onChange={(e) => setLossLock(Math.max(1, Number(e.target.value) || 10))}
                className="block w-20 mt-0.5 px-3 h-10 rounded-md bg-[#131722] border border-white/10 focus:border-[#F23645] focus:outline-none text-sm font-mono text-white" />
            </label>
            {/* 🔁 recovery — always-visible pills (like Penalty), opt-in */}
            <div className="flex flex-col gap-1">
              <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-white/40">🔁 Recovery · win losses back</span>
              <div className="flex items-center gap-1 flex-wrap">
                <button data-testid="arcade-recovery-off" onClick={() => setRecovOn(false)}
                  className={`px-2.5 h-8 rounded-full border text-[10px] font-mono font-black transition-colors ${!recovOn ? "border-[#F23645] bg-[#F23645]/15 text-[#F23645]" : "border-white/15 text-white/40 hover:border-white/35"}`}>
                  OFF
                </button>
                {[3, 4, 5, 6].map((c) => (
                  <button key={c} data-testid={`arcade-recovery-cap-${c}`} onClick={() => { setRecovOn(true); setRecovCap(c); }}
                    className={`px-2.5 h-8 rounded-full border text-[10px] font-mono font-black transition-colors ${recovOn && recovCap === c ? "border-[#F7B731] bg-[#F7B731]/15 text-[#F7B731]" : "border-white/15 text-white/40 hover:border-white/35"}`}>
                    {c}× runs
                  </button>
                ))}
              </div>
              <span data-testid="arcade-recovery-status" className={`text-[8px] font-mono ${recovOn ? (recovStep > 0 ? "text-[#F7B731]" : "text-white/50") : "text-white/35"}`}>
                {!recovOn
                  ? "off for single taps — ⚡ Auto-Play ×3/×5/×10 bursts still auto-recover (loss raises the next play · win → base)"
                  : recovHole > 0.009
                    ? `down $${recovHole.toFixed(2)} · next play $${Math.max(Math.max(0.35, Number(stake) || 0.35), recHoleStake(recovHole)).toFixed(2)} · a win → back to base`
                    : "on · at base — a loss raises ONLY the next play to win it back, a win drops back to base"}
              </span>
              {isReal && (
                <span data-testid="arcade-real-contract-warning" className="text-[8px] font-mono text-[#ff8a95]">
                  ⚠ Each game is a real contract. You can lose your whole stake.
                </span>
              )}
              {recovOn && <span className="text-[8px] font-mono text-[#F23645]/90">⚠ riskier: a loss run stacks fast · cap auto-resets to base</span>}
              {!recovOn && recovOffer && (
                <div data-testid="arcade-recovery-offer" className="flex items-center gap-1.5 flex-wrap rounded-lg border border-[#F7B731]/50 bg-[#F7B731]/10 px-2.5 py-1.5 arc-pop">
                  <span className="text-[9px] font-mono text-[#F7B731] font-bold">🔁 Lost that one — double ONLY the next play?</span>
                  <button data-testid="arcade-recovery-apply" onClick={() => armOneShot(recovOffer.next)}
                    className="px-2.5 h-7 rounded-full bg-[#F7B731] text-[#0B0E14] text-[10px] font-mono font-black hover:brightness-110 active:scale-95 transition">
                    Next at ${recovOffer.next.toFixed(2)}
                  </button>
                  <button data-testid="arcade-recovery-dismiss" onClick={() => setRecovOffer(null)}
                    className="px-2.5 h-7 rounded-full border border-white/20 text-white/55 text-[10px] font-mono font-black hover:border-white/40 transition">
                    Keep base
                  </button>
                  <span className="text-[8px] font-mono text-white/40 w-full">honest math: a win recovers the loss + profit · a miss costs more — your choice</span>
                </div>
              )}
              {!recovOn && oneShot && (
                <span data-testid="arcade-oneshot-armed" className="text-[8px] font-mono text-[#F7B731]">⚡ next play armed at ${oneShot.toFixed(2)} — one play only, then back to base</span>
              )}
            </div>
            <div className="text-right">
              <p className="text-[9px] font-mono uppercase tracking-[0.2em] text-white/40">Streak</p>
              <p data-testid="arcade-streak-counter" className="text-xl font-black font-mono text-[#F7B731] inline-flex items-center gap-1">
                <Flame className={`w-4 h-4 ${streak >= 3 ? "text-[#F7B731]" : "text-white/25"}`} />{streak}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[9px] font-mono uppercase tracking-[0.2em] text-white/40">Session P&L</p>
              <p data-testid="arcade-session-net" className={`text-2xl font-black font-mono ${net >= 0 ? "text-[#089981]" : "text-[#F23645]"}`}>
                {net >= 0 ? "+" : "−"}${Math.abs(net).toFixed(2)}
              </p>
            </div>
            <button data-testid="arcade-reset-session" onClick={resetSession} title="Reset session counters (P&L, streak, score, history)"
              className="w-10 h-10 rounded-md border border-white/10 hover:bg-white/5 flex items-center justify-center transition-colors">
              <RotateCcw className="w-4 h-4 text-white/40" />
            </button>
            <button data-testid="arcade-sound-toggle" onClick={toggleMute}
              className="w-10 h-10 rounded-md border border-white/10 hover:bg-white/5 flex items-center justify-center transition-colors">
              {muted ? <VolumeX className="w-4 h-4 text-white/40" /> : <Volume2 className="w-4 h-4 text-[#2962FF]" />}
            </button>
          </div>
        </div>
        {/* 🛑 loss-lock / forced break banner — real accounts only (demo = practice, no locks) */}
        {isReal && (lockUntil > Date.now() || net - lossBase <= -lossLock) && (
          <div data-testid="arcade-losslock-banner" className="mt-3 rounded-lg border border-[#F23645]/60 bg-[#F23645]/10 px-3 py-2">
            <p className="text-[11px] font-mono font-black text-[#F23645]">
              🛑 LOSS LOCK — down ${Math.abs(net - lossBase).toFixed(2)} · ALL games on a forced 1-hour break
              {lockUntil > Date.now() && <span data-testid="arcade-losslock-countdown"> · {Math.max(1, Math.ceil((lockUntil - Date.now()) / 60000))}m left</span>}
            </p>
            <p className="text-[9px] font-mono text-white/50 mt-0.5">this break is automatic and can't be skipped — chasing losses is exactly how the edge takes everything. Your stats and history stay saved; the arcade reopens when the timer ends.</p>
          </div>
        )}
        {/* 🏁 season meter — GLOBAL hourly play cap across all games (real accounts) */}
        {(() => {
          const demo = authorized && !isReal;
          const nowMs = Date.now();
          const plays = prunePlays(season.plays, nowMs);
          const used = plays.length;
          const capOff = !demo && capOffUntil > nowMs;
          const over = !demo && !capOff && used >= SEASON_MAX;
          const freeMins = used > 0 ? Math.max(1, Math.ceil((plays[0] + SEASON_MS - nowMs) / 60000)) : 0;
          const rearmHrs = capOff ? Math.max(1, Math.ceil((capOffUntil - nowMs) / 3600000)) : 0;
          return (
            <div data-testid="arcade-season-meter" className={`mt-3 rounded-lg border px-3 py-2 ${over ? "border-[#F23645]/50 bg-[#F23645]/10" : capOff ? "border-[#F7B731]/50 bg-[#F7B731]/5" : "border-white/10 bg-[#131722]"}`}>
              <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono">
                <span className="font-black uppercase tracking-wide text-white/70">🏁 Season · all games</span>
                <span data-testid="arcade-season-count" className={over ? "text-[#F23645] font-black" : capOff ? "text-[#F7B731] font-black" : "text-white/70 font-bold"}>
                  {demo ? `${used} plays · 🎓 NO CAP on demo` : capOff ? `${used} plays · ⚠️ CAP OFF` : `${used}/${SEASON_MAX} plays · last 60 min`}
                </span>
                <span className="ml-auto text-white/40">{demo ? "practice freely" : capOff ? `re-arms in ~${rearmHrs}h` : used > 0 ? `next slot frees in ${freeMins}m` : "rolling hour window"}</span>
                {!demo && authorized && (capOff ? (
                  <button data-testid="arcade-cap-rearm-btn" onClick={rearmCap}
                    className="rounded-md border border-[#089981]/60 px-2 py-0.5 text-[9px] font-black text-[#089981] hover:bg-[#089981]/10 transition-colors">✅ RE-ARM NOW</button>
                ) : (
                  <button data-testid="arcade-cap-off-btn" onClick={() => setCapDlg(true)}
                    className="rounded-md border border-white/15 px-2 py-0.5 text-[9px] font-mono text-white/45 hover:bg-white/5 transition-colors">cap settings</button>
                ))}
              </div>
              <div className="mt-1.5 h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div className={`h-full transition-all ${over ? "bg-[#F23645]" : capOff ? "bg-[#F7B731]" : demo ? "bg-[#53b9ff]" : used >= 40 ? "bg-[#F7B731]" : "bg-[#089981]"}`} style={{ width: demo || capOff ? "100%" : `${Math.min(100, (used / SEASON_MAX) * 100)}%` }} />
              </div>
              <p data-testid="arcade-season-note" className="mt-1 text-[9px] font-mono text-white/45">
                {demo
                  ? "🎓 DEMO ACCOUNT — no play caps, no session breaks, no loss lock. It's practice money: test every strategy as hard as you want. Limits switch ON automatically on a real account."
                  : capOff
                    ? "⚠️ You disabled the 50/hour cap and accepted the risk — unlimited plays until it auto re-arms in 24h. The loss lock and stake caps are STILL on. Every extra play feeds the house edge; re-arm anytime."
                    : over
                      ? "⛔ season full — ALL arcade games are paused. Each play frees its slot exactly 1h after it fired (switching games doesn't skip this). Bank what you have and rest."
                      : "why 50/hour across ALL games? every play carries a house edge — run it long enough and it takes your profits back. The hourly season keeps your wins yours."}
              </p>
            </div>
          );
        })()}
        {/* ⚠️ friction dialog — type the exact phrase to switch the cap off */}
        {capDlg && (
          <div data-testid="arcade-capoff-dialog" className="fixed inset-0 z-[90] grid place-items-center bg-black/70 backdrop-blur-sm p-4" onClick={() => { setCapDlg(false); setCapInput(""); }}>
            <div className="w-full max-w-sm rounded-xl border border-[#F23645]/50 bg-[#131722] p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
              <h4 className="text-sm font-black text-[#F23645]">⚠️ Turn OFF the 50 plays/hour cap?</h4>
              <ul className="text-[10px] font-mono text-white/60 space-y-1 list-disc pl-4">
                <li>This cap exists because <b className="text-white/80">volume is what loses money</b> — every play carries a house edge.</li>
                <li>With the cap off you can play <b className="text-[#F23645]">unlimited real-money rounds</b> for 24 hours.</li>
                <li>It <b className="text-white/80">re-arms automatically after 24h</b>, and you can re-arm it sooner anytime.</li>
                <li>Your 🛑 loss lock, 10× recovery cap and 10% balance stake cap <b className="text-[#089981]">stay ON</b> — those never switch off.</li>
              </ul>
              <p className="text-[10px] font-mono text-white/70">Type <b className="text-[#F7B731]">{CAPOFF_PHRASE}</b> to confirm:</p>
              <input data-testid="arcade-capoff-input" value={capInput} onChange={(e) => setCapInput(e.target.value)}
                placeholder={CAPOFF_PHRASE} autoFocus
                className="w-full h-10 rounded-lg border border-white/15 bg-[#0d1117] px-3 text-xs font-mono text-white placeholder:text-white/20 focus:border-[#F23645]/60 outline-none" />
              <div className="flex gap-2">
                <button data-testid="arcade-capoff-cancel" onClick={() => { setCapDlg(false); setCapInput(""); }}
                  className="flex-1 h-10 rounded-lg border border-white/15 text-xs font-bold text-white/70 hover:bg-white/5 transition-colors">Keep me safe</button>
                <button data-testid="arcade-capoff-confirm" disabled={capInput.trim().toUpperCase() !== CAPOFF_PHRASE} onClick={disableCap}
                  className="flex-1 h-10 rounded-lg bg-[#F23645] text-xs font-black text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#d12b39] transition-colors">Disable for 24h</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {!authorized && (
        <div className="rounded-xl border border-white/10 bg-[#131722] p-5 text-center">
          <p className="text-xs font-mono text-white/55 mb-3">Connect your Deriv account to play with real contracts</p>
          <button data-testid="arcade-connect-btn" onClick={onConnect}
            className="px-5 h-11 rounded-lg bg-[#2962FF] hover:bg-[#1E4ECA] text-white text-sm font-bold transition-colors">Connect account</button>
        </div>
      )}

      {/* live market digit tape — transparency, not prediction */}
      <DigitTape digits={digits} total={total} marketLabel={liveMkt.label} />

      {/* (global hot bar removed — Penalty now carries its own Signal Engine scanner next to the shoot buttons) */}

      {/* game picker */}
      <div data-testid="arcade-picker" className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {GAMES.map(({ key, label, Icon, desc }) => (
          <button key={key} data-testid={`arcade-pick-${key}`} onClick={() => setGame(key)}
            className={`rounded-xl border p-4 text-left transition-colors ${game === key
              ? "border-[#2962FF] bg-[#2962FF]/5"
              : "border-white/10 bg-[#131722] hover:border-white/25"}`}
            style={game === key ? { boxShadow: "inset 0 0 24px rgba(41,98,255,.12)" } : {}}>
            <Icon className={`w-5 h-5 mb-2 ${game === key ? "text-[#2962FF]" : "text-white/40"}`} />
            <p className="text-[12px] font-black tracking-tight text-white leading-tight">{label}</p>
            <p className="text-[9px] font-mono text-white/40 leading-tight mt-0.5">{desc}</p>
          </button>
        ))}
      </div>

      {/* active game */}
      <div className="rounded-xl border border-white/10 bg-[#131722] p-5 sm:p-6">
        {game === "penalty" && (
          <PenaltyParty party={party} accountId={accountId} token={session?.token} isReal={isReal} onChanged={loadParty} onConnect={onConnect} />
        )}
        {game === "penalty" && stLoaded && <PenaltyShootout runFlip={runFlip} runContract={runContract} busy={busy} report={report} celebrate={celebrate} digits={digits} initial={pScore} onPersist={setPScore} baseStake={Math.max(0.35, Number(stake) || 0.35)} logExternal={logExternal} />}
        {game === "jackpot" && <StreakJackpot runFlip={runFlip} busy={busy} report={report} celebrate={celebrate} />}
        {game === "redblack" && <RedBlackFlip runFlip={runFlip} busy={busy} />}
        {game === "carrace" && <CarRace runContract={runContract} busy={busy} baseStake={Math.max(0.35, Number(stake) || 0.35)} logExternal={logExternal} />}
        {game === "bball" && <BasketballShot runContract={runContract} busy={busy} baseStake={Math.max(0.35, Number(stake) || 0.35)} logExternal={logExternal} />}
        {game === "gems" && <GemRush runContract={runContract} busy={busy} balance={balance} />}
        {game === "boxing" && <BoxingKO runContract={runContract} busy={busy} />}
        {game === "lucky" && <LuckyNumber runContract={runContract} busy={busy} />}
        {game === "greenlight" && <GreenLight runContract={runContract} busy={busy} />}
        {game === "dice" && <DiceDuel runContract={runContract} busy={busy} />}
      </div>

      {/* session history — grouped per game so you SEE which game pays you */}
      {(history.length > 0 || Object.keys(gameStats).length > 0 || lastSession) && (
        <div data-testid="arcade-history" className="rounded-xl border border-white/10 bg-[#131722] p-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-xs font-black tracking-tight text-white">Session history <span className="text-[9px] font-mono font-normal text-white/40">(also saved to your main History tab)</span></p>
            {lastSession && (
              <span data-testid="arcade-last-session" className={`ml-auto px-2 py-1 rounded-md border text-[9px] font-mono font-bold ${lastSession.net >= 0 ? "border-[#089981]/40 bg-[#089981]/10 text-[#089981]" : "border-[#F23645]/40 bg-[#F23645]/10 text-[#F23645]"}`}>
                last session: {lastSession.net >= 0 ? "+" : "−"}${Math.abs(lastSession.net).toFixed(2)} · {lastSession.plays} plays
                {lastSession.games?.[0] ? ` · best: ${lastSession.games[0].label} (${lastSession.games[0].net >= 0 ? "+" : "−"}$${Math.abs(lastSession.games[0].net).toFixed(2)})` : ""}
              </span>
            )}
          </div>

          {(history.length > 0 || Object.keys(gameStats).length > 0) && (() => {
            // 📊 chips come from TRUE running totals (gameStats) — never affected by the list cap
            const games = Object.entries(gameStats).map(([label, g]) => ({ label, ...g })).sort((a, b) => b.net - a.net);
            const totalPlays = games.reduce((s, g) => s + g.w + g.l, 0);
            const shown = histFilter ? history.filter((h) => (h.label || "Game") === histFilter) : history;
            return (
              <>
                {/* 💰 per-game P&L — tap a game to filter the list */}
                <div data-testid="arcade-history-bygame" className="flex items-center gap-1.5 flex-wrap">
                  <button data-testid="arcade-histfilter-all" onClick={() => setHistFilter(null)}
                    className={`px-2 py-1 rounded-md border text-[9px] font-mono font-black transition-colors ${!histFilter ? "border-[#2962FF] bg-[#2962FF]/15 text-[#53b9ff]" : "border-white/15 text-white/45 hover:border-white/35"}`}>
                    ALL · {totalPlays}
                  </button>
                  {games.map((g, i) => (
                    <button key={g.label} data-testid={`arcade-histfilter-${g.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      onClick={() => setHistFilter(histFilter === g.label ? null : g.label)}
                      className={`px-2 py-1 rounded-md border text-[9px] font-mono font-bold transition-colors ${histFilter === g.label
                        ? "border-[#F7B731] bg-[#F7B731]/15 text-[#F7B731]"
                        : "border-white/10 text-white/60 hover:border-white/30"}`}>
                      {i === 0 && g.net > 0 ? "👑 " : ""}{g.label} · {g.w}W/{g.l}L ·{" "}
                      <span className={g.net >= 0 ? "text-[#089981]" : "text-[#F23645]"}>{g.net >= 0 ? "+" : "−"}${Math.abs(g.net).toFixed(2)}</span>
                    </button>
                  ))}
                </div>
                <p className="text-[8.5px] font-mono text-white/35">👑 = your top earner THIS session · totals count EVERY play this session (the list below shows the last 100) · recent results only, every game stays ~50/50 — this never predicts the next play</p>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {shown.length === 0 && histFilter && (
                    <p className="text-[9px] font-mono text-white/40 px-1 py-2">older {histFilter} trades rolled off the recent list — the totals in the chip above stay exact</p>
                  )}
                  {shown.map((h, i) => (
                    <div key={i} className="flex items-center gap-2 text-[10px] font-mono rounded border border-white/5 bg-[#0B0E14] px-2 py-1.5">
                      <span className="text-white/35">{h.t}</span>
                      <span className="text-white">{h.label}</span>
                      <span className="text-white/50">{h.side} → {h.digit ?? "?"}</span>
                      <span className="text-white/35">${Number(h.stake || 0).toFixed(2)}</span>
                      <span className={`ml-auto font-bold ${h.won ? "text-[#089981]" : "text-[#F23645]"}`}>
                        {h.won ? "+" : "−"}${Math.abs(Number(h.profit) || 0).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
          {history.length === 0 && Object.keys(gameStats).length === 0 && lastSession && (
            <div data-testid="arcade-lastsession-games" className="flex items-center gap-1.5 flex-wrap">
              {(lastSession.games || []).map((g, i) => (
                <span key={g.label} className="px-2 py-1 rounded-md border border-white/10 text-[9px] font-mono font-bold text-white/60">
                  {i === 0 && g.net > 0 ? "👑 " : ""}{g.label} · {g.w}W/{g.l}L ·{" "}
                  <span className={g.net >= 0 ? "text-[#089981]" : "text-[#F23645]"}>{g.net >= 0 ? "+" : "−"}${Math.abs(g.net).toFixed(2)}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* leaderboard — weekly race */}
      <div data-testid="arcade-leaderboard" className="rounded-xl border border-white/10 bg-[#131722] p-4">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <p className="text-xs font-black tracking-tight text-white inline-flex items-center gap-1.5">
            <Crown className="w-4 h-4 text-[#F7B731]" /> Weekly arcade race
          </p>
          <span className="text-[9px] font-mono text-white/40">resets every Monday 00:00 UTC — new week, new crown</span>
        </div>
        <TradersByCountry className="mb-3" />

        {/* claim your nickname right here */}
        {accountId && (
          <div className="flex items-center gap-2 flex-wrap mb-3 rounded-lg border border-white/5 bg-[#0B0E14] p-2.5">
            <p data-testid="arcade-nickname-current" className="text-[10px] font-mono text-white/55">
              Your name on the board: <span className="text-white font-bold">{nick || `Trader …${String(accountId).slice(-4)}`}</span>
            </p>
            <div className="ml-auto flex items-center gap-2">
              <input data-testid="arcade-nickname-input" value={nickInput} maxLength={20}
                onChange={(e) => setNickInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveNick(); }}
                placeholder={nick ? "change nickname…" : "claim a nickname…"}
                className="w-40 px-3 h-9 rounded-md bg-[#131722] border border-white/10 focus:border-[#2962FF] focus:outline-none text-[11px] font-mono text-white" />
              <button data-testid="arcade-nickname-save-btn" onClick={saveNick} disabled={!nickInput.trim()}
                className="px-3 h-9 rounded-md bg-[#2962FF] hover:bg-[#1E4ECA] text-white text-[11px] font-bold transition-colors disabled:opacity-40">
                Claim
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {LB_SECTIONS.map(({ key, label, unit }) => (
            <div key={key} className="rounded-lg border border-white/5 bg-[#0B0E14] p-3">
              <p className="text-[9px] font-mono uppercase tracking-[0.25em] text-white/45 mb-2">{label}</p>
              {(board?.[key] || []).length === 0 ? (
                <p className="text-[10px] font-mono text-white/30">no entries this week — be the first</p>
              ) : (
                <div className="space-y-1">
                  {(board?.[key] || []).map((r, i) => (
                    <div key={i} className="flex items-center gap-2 text-[10px] font-mono">
                      <span className={`w-4 font-black ${i === 0 ? "text-[#F7B731]" : "text-white/35"}`}>{i + 1}</span>
                      <span className="text-white/80 truncate inline-flex items-center gap-1">
                        {r.country && <span className="text-[11px] leading-none">{flagEmoji(r.country)}</span>}
                        {r.name}
                        {r.champ && <Crown data-testid={`arcade-champ-${key}`} className="w-3 h-3 text-[#F7B731] shrink-0" style={{ filter: "drop-shadow(0 0 4px rgba(247,183,49,.7))" }} />}
                      </span>
                      <span className="ml-auto font-bold text-[#2962FF]">{r.value}{unit === "×" ? "×" : ` ${unit}`}</span>
                    </div>
                  ))}
                </div>
              )}
              {board?.champions?.[key] && (
                <p className="text-[9px] font-mono text-white/35 mt-2 inline-flex items-center gap-1">
                  <Crown className="w-3 h-3 text-[#F7B731]" /> last week's champ: <span className="text-[#F7B731]">{board.champions[key]}</span>
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      <p className="text-[9px] font-mono text-white/35 text-center">
        Every play is a real Deriv Even/Odd contract with a small house edge — play with a budget and bank your greens.
      </p>
      <EmailOptIn accountId={accountId || ""} />
    </div>
  );
};

export default ArcadeHub;
